import React from "react";
import { useLocation, useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import Stack from "@mui/material/Stack";
import { useMutation, useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import { GET_CITATION_BY_ID, GetCitationByIdData, GetCitationByIdVars } from "common/graphql/queries/GetCitation";
import {
  VOID_CITATION,
  VoidCitationData,
  VoidCitationVars,
  RECORD_CITATION_PAYMENT,
  RecordCitationPaymentData,
  RecordCitationPaymentVars,
} from "common/graphql";
import LocalLevelError from "@app.automotus.io/components/common/LocalLevelError";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { CitationsLocationState } from "..";
import { sanitizeInternalPath } from "common/helpers";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { CitationPageContent } from "./CitationPageContent";

export const Citation: React.FC = () => {
  const { citationId = "" } = useParams<{ citationId: string }>();
  const { data, loading, error, refetch } = useQuery<GetCitationByIdData, GetCitationByIdVars>(GET_CITATION_BY_ID, {
    variables: { id: citationId },
    onError(err) {
      console.error(`failed to fetch citation by id ${citationId}`, err);
    },
  });

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { publishSnackbarMessage } = useSnackPackContext();

  const [voidCitation, { loading: voidLoading }] = useMutation<VoidCitationData, VoidCitationVars>(VOID_CITATION);
  const [recordCitationPayment, { loading: recordPaymentLoading }] = useMutation<
    RecordCitationPaymentData,
    RecordCitationPaymentVars
  >(RECORD_CITATION_PAYMENT);

  const citation = data?.citation ?? null;
  const showVoidButton = !!(
    citation &&
    !citation.voidedAt &&
    !citation.paidAt &&
    citation.noticeIntegrationPartner?.isVoidSubmittedNoticeAllowed
  );
  const showRecordPaymentButton = !!(
    citation &&
    !citation.voidedAt &&
    !citation.paidAt &&
    citation.noticeIntegrationPartner?.isApplyPaymentToNoticeAllowed
  );

  const handleVoidSubmit = async ({ reason, voidedAt }: { reason: string; voidedAt: Date }) => {
    try {
      await voidCitation({
        variables: { id: citationId, reason, voidedAt },
      });
      publishSnackbarMessage({ severity: "success", message: "Citation voided" });
      refetch();
    } catch (err) {
      console.error("Failed to void citation", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to void citation" });
      throw err;
    }
  };

  const handleRecordPaymentSubmit = async ({ paidAmountCents, paidAt }: { paidAmountCents: number; paidAt: Date }) => {
    try {
      await recordCitationPayment({
        variables: { id: citationId, paidAmount: paidAmountCents, paidAt },
      });
      publishSnackbarMessage({ severity: "success", message: "Payment recorded" });
      refetch();
    } catch (err) {
      console.error("Failed to record payment", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to record payment" });
      throw err;
    }
  };

  const locationState = location.state as CitationsLocationState | null;
  // `returnTo`, set on the link itself, survives opening in a new tab — unlike `state` above,
  // which only exists in the originating tab's in-memory history — so it takes priority when
  // present. Sanitized since it's user-controllable (a crafted link's query param) and gets used
  // directly as a Link target below.
  const backButtonLink =
    sanitizeInternalPath(searchParams.get("returnTo")) ||
    `..?page=${locationState?.citationTablePage || 0}&pageSize=${locationState?.citationTablePageSize || 31}${
      locationState?.citationTableSortStr ? `&sort=${encodeURIComponent(locationState?.citationTableSortStr)}` : ""
    }`;

  if (error) {
    return (
      <>
        <IconButton component={RouterLink} to={backButtonLink}>
          <ArrowBackIosIcon />
        </IconButton>
        <Stack spacing={3} width={"100%"}>
          <Box>
            <LocalLevelError onTryAgain={refetch} />
          </Box>
        </Stack>
      </>
    );
  }

  return (
    <CitationPageContent
      citationId={citationId}
      citation={citation}
      loading={loading}
      backButtonLink={backButtonLink}
      showVoidButton={showVoidButton}
      showRecordPaymentButton={showRecordPaymentButton}
      onVoidSubmit={handleVoidSubmit}
      onRecordPaymentSubmit={handleRecordPaymentSubmit}
      voidSubmitting={voidLoading}
      recordPaymentSubmitting={recordPaymentLoading}
    />
  );
};

export default Citation;
